import { Deal } from '@types';

export const groupFinancialsByCategory = (metrics: Deal['financial_metrics']) => {

  return metrics.reduce((acc, metric) => {
    const group = metric.category.includes('Revenue') ? 'Revenue' : metric.category.includes('Expense') ? 'Expenses' : metric.category.includes('Income') ? 'Income' : 'Other';

    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(metric);
    return acc;
  }, {} as Record<string, typeof metrics>);
};

export const validateMetricValue = (value: string | number): boolean => {
  const numValue = typeof value === 'string' ? Number(value.replace(/[^0-9.-]/g, '')) : value;
  return !isNaN(numValue);
};

// Format currency with compact notation for large numbers (USD only)
export const formatCurrency = (value?: number, currency: 'USD' | 'EUR' = 'USD'): string => {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';

  if (currency !== 'USD') {
    console.warn('Multi-currency support not yet implemented. Displaying as USD.');
  }

  if (Math.abs(value) >= 1000000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2
    }).format(value);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format percentage
export const formatPercent = (value?: number): string => {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  return `${(value * 100).toFixed(1)}%`;
};

// ============================================================================
// SAFE MATH UTILITIES - Prevent NaN propagation
// ============================================================================

/**
 * Safe math operations that handle null/undefined/NaN gracefully
 */
export const safeMath = {
  divide: (a: number | null | undefined, b: number | null | undefined): number | null => {
    const numA = a ?? null;
    const numB = b ?? null;
    if (numA === null || numB === null || numB === 0) return null;
    const result = numA / numB;
    return isFinite(result) ? result : null;
  },

  multiply: (a: number | null | undefined, b: number | null | undefined): number | null => {
    const numA = a ?? null;
    const numB = b ?? null;
    if (numA === null || numB === null) return null;
    const result = numA * numB;
    return isFinite(result) ? result : null;
  },

  add: (...values: (number | null | undefined)[]): number | null => {
    const validValues = values.filter(v => v !== null && v !== undefined && isFinite(v)) as number[];
    return validValues.length > 0 ? validValues.reduce((sum, v) => sum + v, 0) : null;
  },

  subtract: (a: number | null | undefined, b: number | null | undefined): number | null => {
    const numA = a ?? null;
    const numB = b ?? null;
    if (numA === null || numB === null) return null;
    const result = numA - numB;
    return isFinite(result) ? result : null;
  }
};

// ============================================================================
// ENHANCED FORMATTING WITH NULL HANDLING
// ============================================================================

export interface FormattedValue {
  display: string;
  raw: number | null;
  status: 'valid' | 'zero' | 'missing' | 'error';
}

/**
 * Enhanced currency formatter that distinguishes between zero and missing data
 */
export const formatCurrencySafe = (
  value?: number | null,
  options?: {
    showZeroAs?: 'zero' | 'dash' | 'na';
    showMissingAs?: string;
    currency?: 'USD' | 'EUR';
  }
): FormattedValue => {
  const defaults = {
    showZeroAs: 'zero' as const,
    showMissingAs: 'No Data',
    currency: 'USD' as const,
    ...options
  };

  // Handle missing/null/undefined
  if (value === undefined || value === null) {
    return {
      display: defaults.showMissingAs,
      raw: null,
      status: 'missing'
    };
  }

  // Handle NaN/Infinity
  if (isNaN(value) || !isFinite(value)) {
    return {
      display: 'Invalid',
      raw: null,
      status: 'error'
    };
  }

  // Handle zero
  if (value === 0) {
    const zeroDisplay = {
      zero: '$0',
      dash: '—',
      na: 'N/A'
    }[defaults.showZeroAs];

    return {
      display: zeroDisplay,
      raw: 0,
      status: 'zero'
    };
  }

  // Format valid number (USD only — multi-currency not yet implemented)
  if (defaults.currency !== 'USD') {
    console.warn('Multi-currency support not yet implemented. Displaying as USD.');
  }

  let formatted: string;
  if (Math.abs(value) >= 1000000) {
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2
    }).format(value);
  } else {
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  return {
    display: formatted,
    raw: value,
    status: 'valid'
  };
};

/**
 * Enhanced percentage formatter that distinguishes between zero and missing data
 */
export const formatPercentSafe = (
  value?: number | null,
  options?: {
    showZeroAs?: 'zero' | 'dash' | 'na';
    showMissingAs?: string;
    decimals?: number;
  }
): FormattedValue => {
  const defaults = {
    showZeroAs: 'zero' as const,
    showMissingAs: 'No Data',
    decimals: 1,
    ...options
  };

  // Handle missing/null/undefined
  if (value === undefined || value === null) {
    return {
      display: defaults.showMissingAs,
      raw: null,
      status: 'missing'
    };
  }

  // Handle NaN/Infinity
  if (isNaN(value) || !isFinite(value)) {
    return {
      display: 'Invalid',
      raw: null,
      status: 'error'
    };
  }

  // Handle zero
  if (value === 0) {
    const zeroDisplay = {
      zero: '0%',
      dash: '—',
      na: 'N/A'
    }[defaults.showZeroAs];

    return {
      display: zeroDisplay,
      raw: 0,
      status: 'zero'
    };
  }

  // Format valid number
  const formatted = `${(value * 100).toFixed(defaults.decimals)}%`;

  return {
    display: formatted,
    raw: value,
    status: 'valid'
  };
};

/**
 * Validate if a value is a valid number (not null, NaN, or Infinity)
 */
export const isValidNumber = (value: any): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num !== null && isFinite(num);
};
